import moment from 'moment';
import { pNum } from '../db/company';
import { TIER3, resolveTier3 } from './billingTier3';

// ── The attendance roster ──
//
// Project controls keeps "Attendance & Force Report - Master.xlsx" on the job
// subsite, one row per person. Two of its columns are better truth than
// anything on the HeavyJob card: "Days or Nights" (a clerk on days routinely
// keys the night crew's time onto one card) and "Rate Sheet Billing
// Classification" (kept current here, while the field workbook's Tier 4 goes
// stale). Both helpers below read this job's rows first and a sister job's
// rows second — one workbook covers 54026070 and 54026071, and its Job column
// says where a person is budgeted, not whose card their hours land on.

const rosterPick = (roster, job, valueOf) => {
    const mine = {};
    const sister = {};
    (roster?.roster || []).forEach((r) => {
        const v = valueOf(r);
        if (!r.id || !v) return;
        const own = !r.job || String(r.job) === String(job);
        (own ? mine : sister)[String(r.id)] = v;
    });
    return { ...sister, ...mine };
};

/** staffId -> 'Day' | 'Night'. People the sheet doesn't place are absent. */
export const rosterShiftOf = (roster, job) => rosterPick(roster, job,
    (r) => ((r.shift === 'Day' || r.shift === 'Night') ? r.shift : ''));

/** staffId -> billed classification, from the roster's rate-sheet column. */
export const rosterClsOf = (roster, job) => rosterPick(roster, job,
    (r) => String(r.billCls || '').trim());

/**
 * The classification map ticketFrom applies: the roster's rate-sheet class
 * wins, the field workbook's Tier 4 fills the gaps.
 */
export const staffClsOf = (tier4, rosterCls) => ({ ...(tier4 || {}), ...(rosterCls || {}) });

// Daily labour helpers for the Billing tab's Time Card Approval view.
//
// This module used to also render a standalone Daily Labor Expense Report;
// that view is gone. What remains is the HEET → deliverable plumbing the
// Time Card Approval tracker builds its exports from: the per-person pivot
// (peopleFrom), the Tier 3 roll-up the client's Daily Force Report counts by
// (rollUp — see billingTier3.js for the four-tier hierarchy), the field
// ticket pivot (ticketFrom), and the two client-workbook file builders.
//
// The source is HEET, not JDE cost history: timecards reach HEET the morning
// after the field enters them, days before the GL posts, which is the only
// reason a deliverable for yesterday can exist at all.
//
// Hours are Calculated_Hours per Record_Type — a 10-hour shift arrives as a
// Regular row plus an Overtime row plus burden rows that all repeat the shift
// length in Regular_Hours, so anything that sums Regular_Hours double counts.
// Equipment rows carry no employee and are not labour; they are dropped here.

const LABOR_TYPES = ['Regular', 'Overtime', 'Per Diem', 'Rig Pay', 'Truck Pay', 'Tax/Fringe', 'Corp OH'];
// Straight time and overtime are the only types carrying hours; the rest are
// money-only adders that still belong in the day's labour cost.
const ADDER_TYPES = ['Per Diem', 'Rig Pay', 'Truck Pay'];
const BURDEN_TYPES = ['Tax/Fringe', 'Corp OH'];

const num = (n) => pNum(n) || 0;

/**
 * Fold the day's HEET rows into one record per person.
 *
 * Exported for the tests: the Record_Type pivot is the part worth pinning
 * down, because getting it wrong is how hours end up doubled.
 */
export const peopleFrom = (rows, ratemap) => {
    const by = {};
    (rows || []).forEach((r) => {
        if (!LABOR_TYPES.includes(r.Record_Type)) return;
        // Equipment, and any stray row with no employee, is not labour.
        const id = r.Staff_ID;
        if (!id) return;
        if (!by[id]) {
            by[id] = {
                id,
                name: r.Staff_Name || '',
                foreman: r.Foreman_Name || '',
                payClass: r.Pay_Class_Description || '',
                payCode: r.Pay_Class_Code || '',
                costCodes: {},
                tags: {},
                tm: false,
                approved: true,
                st: 0, ot: 0, adders: 0, burden: 0, base: 0,
            };
        }
        const p = by[id];
        // Per Diem rows carry no pay class, so take it from whichever row has
        // one rather than letting row order decide.
        if (!p.payClass && r.Pay_Class_Description) p.payClass = r.Pay_Class_Description;
        if (!p.foreman && r.Foreman_Name) p.foreman = r.Foreman_Name;
        if (r.Cost_Code) p.costCodes[r.Cost_Code] = r.Cost_Code_Description || '';
        if (r.Hour_Tag_Description) p.tags[r.Hour_Tag_Description] = true;
        if (Number(r.Is_TM) === 1) p.tm = true;
        if (Number(r.Is_Approved) !== 1) p.approved = false;
        const h = num(r.Hours);
        const a = num(r.Amount);
        if (r.Record_Type === 'Regular') { p.st += h; p.base += a; }
        else if (r.Record_Type === 'Overtime') { p.ot += h; p.base += a; }
        else if (ADDER_TYPES.includes(r.Record_Type)) p.adders += a;
        else if (BURDEN_TYPES.includes(r.Record_Type)) p.burden += a;
    });
    return Object.values(by).map((p) => {
        const m = resolveTier3(p.payClass, ratemap);
        return {
            ...p,
            hours: p.st + p.ot,
            cost: p.base + p.adders + p.burden,
            t3: m.t3,
            cls: m.cls,
            reason: m.reason,
            costCode: Object.keys(p.costCodes).sort().join(', '),
            tag: Object.keys(p.tags).sort().join(', '),
        };
    }).sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

/**
 * Roll people into the Tier 3 buckets, Direct first then Indirect, with an
 * "Unclassified" bucket at the end so nobody's hours vanish off the report.
 * Empty buckets are dropped — the client's form prints all 19, but a screen
 * full of zero rows reads worse than only the ones actually manned.
 */
export const rollUp = (people) => {
    const buckets = {};
    const add = (key, label, dir, p) => {
        if (!buckets[key]) buckets[key] = { key, label, dir, head: 0, st: 0, ot: 0, cost: 0, bill: 0, people: [] };
        const b = buckets[key];
        b.head += 1; b.st += p.st; b.ot += p.ot; b.cost += p.cost; b.bill += p.bill || 0;
        b.people.push(p);
    };
    people.forEach((p) => {
        if (p.t3) add(String(p.t3.code), p.t3.name, p.t3.dir, p);
        else add('unclassified', 'Unclassified', 'Unclassified', p);
    });
    const order = TIER3.map((t) => String(t.code)).concat(['unclassified']);
    return order.map((k) => buckets[k]).filter(Boolean).map((b) => ({
        ...b,
        people: b.people.sort((x, y) => String(x.name).localeCompare(String(y.name))),
    }));
};

// ── The approved time card ──
//
// readApprovedTimeCard returns project controls' reviewed week: one row per
// person with a shift, a classification and a total per date. That total is
// what the client is billed; the HeavyJob cards are only how the field keyed
// it. Reconciling the two on the rows — rather than on the finished ticket —
// means the export, the QA checks, the PDF and the summaries all read the
// approved number without knowing this step happened.

/** staffId -> 'Day' | 'Night' as the approved card bills them. */
export const cardShiftOf = (card) => Object.fromEntries(
    (card?.labor || []).filter((p) => p.id && p.shift).map((p) => [String(p.id), p.shift]),
);

/** staffId -> the classification the approved card bills them as. */
export const cardClsOf = (card) => Object.fromEntries(
    (card?.labor || []).filter((p) => p.id && p.cls).map((p) => [String(p.id), String(p.cls).trim()]),
);

// Hours come off these phases first. Orientation and other project support are
// what a reviewed card almost always backs out, and taking them from the phase
// the work was actually booked to would distort the client's cost report.
const CUT_FIRST = ['100105', '100201'];

const HOUR_ROW = (r) => r.Record_Type === 'Regular' || r.Record_Type === 'Overtime';

/**
 * Reconcile one day's HEET rows to the approved time card.
 *
 * Over the card: hours come off CUT_FIRST phases first, overtime before
 * straight time within a phase, and rows emptied out are dropped.
 * Under the card: the balance is added as straight time on the phase that
 * person worked most that day, else most that week. Someone the card bills
 * with no HeavyJob row at all (a salaried lead who keys no card) is carried
 * onto the ticket with a blank phase, which the unclassified check then
 * surfaces — the phase is never guessed.
 *
 * Returns the rows unchanged when there is no card for the date, so a week
 * project controls has not reviewed yet still bills off HeavyJob.
 */
export const applyApprovedCard = (rows, card, date) => {
    const people = (card?.labor || []).filter((p) => p.id);
    if (!date || !people.length) return rows || [];
    // An open week's card is built off HeavyJob part-way through (WE 9/20/26:
    // uploaded with 9/17 on blank). A day nobody has hours on is a day the card
    // does not cover yet, not a day the whole crew was struck off — HeavyJob
    // stands until the card is rebuilt.
    if (!people.some((p) => num(p.days?.[date]) > 0.005)) return rows || [];

    const byId = {};
    (rows || []).forEach((r) => {
        const id = String(r.Staff_ID || '');
        if (id) (byId[id] = byId[id] || []).push(r);
    });

    // The phase each person leans on, for placing hours the cards don't cover.
    const weekPhase = {};
    (rows || []).forEach((r) => {
        if (!HOUR_ROW(r) || !r.Cost_Code) return;
        const id = String(r.Staff_ID || '');
        const w = weekPhase[id] || (weekPhase[id] = {});
        w[r.Cost_Code] = (w[r.Cost_Code] || 0) + num(r.Hours);
    });
    const topPhase = (id) => {
        const w = weekPhase[id] || {};
        return Object.keys(w).sort((a, b) => w[b] - w[a])[0] || '';
    };

    const out = [];
    const touched = new Set();
    people.forEach((p) => {
        const id = String(p.id);
        touched.add(id);
        // The card's name is the one the client signs against — HeavyJob
        // carries legal names (Hirshell Guynes) where the site, the sign-in
        // sheets and the reviewed card all say Jance.
        const mine = (byId[id] || []).map((r) => ((p.name && r.Staff_Name !== p.name)
            ? { ...r, Staff_Name: p.name } : r));
        const hourRows = mine.filter(HOUR_ROW);
        const current = hourRows.reduce((a, r) => a + num(r.Hours), 0);
        const target = num(p.days?.[date]);

        if (Math.abs(current - target) <= 0.005) { out.push(...mine); return; }

        if (current > target) {
            let cut = current - target;
            const rank = (c) => {
                const i = CUT_FIRST.indexOf(String(c));
                return i < 0 ? CUT_FIRST.length : i;
            };
            const order = [...hourRows].sort((a, b) => (rank(a.Cost_Code) - rank(b.Cost_Code))
                || ((a.Record_Type === 'Overtime' ? 0 : 1) - (b.Record_Type === 'Overtime' ? 0 : 1)));
            const trimmed = new Map();
            order.forEach((r) => {
                if (cut <= 0.005) return;
                const take = Math.min(num(r.Hours), cut);
                cut -= take;
                trimmed.set(r, num(r.Hours) - take);
            });
            mine.forEach((r) => {
                if (!trimmed.has(r)) { out.push(r); return; }
                const left = trimmed.get(r);
                if (left > 0.005) out.push({ ...r, Hours: left });
            });
            return;
        }

        // Under the card: one straight-time row carries the balance.
        const dayPhase = hourRows.reduce((best, r) => {
            const h = num(r.Hours);
            return h > best.h ? { code: r.Cost_Code, h } : best;
        }, { code: '', h: 0 }).code;
        const base = hourRows[0] || mine[0] || {};
        out.push(...mine, {
            ...base,
            Record_Type: 'Regular',
            Staff_ID: p.id,
            Staff_Name: base.Staff_Name || p.name || '',
            Pay_Class_Description: base.Pay_Class_Description || p.cls || '',
            Cost_Code: dayPhase || topPhase(id) || '',
            Cost_Code_Description: dayPhase ? (base.Cost_Code_Description || '') : '',
            Hours: target - current,
            Amount: 0,
            Equipment_Code: null,
        });
    });

    // Rows for people the card does not bill at all (equipment, and anyone
    // project controls struck off the week) pass through untouched.
    (rows || []).forEach((r) => {
        const id = String(r.Staff_ID || '');
        if (!id || !touched.has(id)) out.push(r);
    });
    return out;
};

/**
 * Pivot the day's HEET rows into the client's Multi Phase Field Ticket shape:
 * one row per person (and per equipment unit) with hours split across the
 * cost codes worked that day. The VoltaGrid ticket is exactly this matrix —
 * columns are the job's WBS phase codes, which HEET's Cost_Code already is —
 * so the export can replace hand-copying the HeavyJob export into the ticket.
 *
 * Exported for the tests: the pivot repeats peopleFrom's Record_Type
 * discipline (Calculated_Hours per type, never Regular_Hours) and getting it
 * wrong doubles hours on the client's ticket.
 *
 * opts.overrides is the Time Card Approval tab's per-day shift corrections
 * ({ staffId: 'Day' | 'Night' }): the HCSS feed says which card the hours
 * were entered on, not which crew the person actually worked, and the person
 * fixing that in the preview needs the fix to stick to every export.
 *
 * opts.equipName is (unit, description) => { name }: the rate sheet item a
 * unit prints under (billingEquipNames equipNamer). The ticket is a client
 * document, so equipment reads the way the rate sheet names it; the feed's
 * own text is kept on the row as `src`. A unit it cannot name keeps its text.
 */
export const ticketFrom = (rows, ratemap, opts = {}) => {
    const equipName = typeof opts.equipName === 'function' ? opts.equipName : null;
    const codes = {};       // Cost_Code -> description
    const labor = {};       // Staff_ID -> person
    const equipment = {};   // Equipment_Code -> unit
    (rows || []).forEach((r) => {
        if (!r.Cost_Code) return;
        if (r.Record_Type === 'Equipment') {
            const key = String(r.Equipment_Code || r.Equipment_Code_Description || 'unknown').trim();
            // A unit with no hours is not on the ticket — HEET carries a
            // 0-hour line for a truck parked on the card (S19-F1680 on
            // 54026071 9/21/26 printed as "1 unit —  hours").
            if (!num(r.Hours)) return;
            if (!equipment[key]) {
                const unit = String(r.Equipment_Code || '').trim();
                const raw = r.Equipment_Code_Description || '';
                const named = equipName ? equipName(unit, raw) : null;
                equipment[key] = {
                    unit,
                    desc: (named && named.name) || raw,
                    src: raw,
                    rental: Number(r.Is_Rental) === 1,
                    hours: {}, total: 0,
                };
            }
            const e = equipment[key];
            const h = num(r.Hours);
            if (!h) return;
            e.hours[r.Cost_Code] = (e.hours[r.Cost_Code] || 0) + h;
            e.total += h;
            if (!codes[r.Cost_Code]) codes[r.Cost_Code] = r.Cost_Code_Description || '';
            return;
        }
        if (!LABOR_TYPES.includes(r.Record_Type)) return;
        const id = r.Staff_ID;
        if (!id) return;
        if (!labor[id]) {
            labor[id] = {
                id,
                name: r.Staff_Name || '',
                payClass: r.Pay_Class_Description || '',
                crewLead: r.Foreman_Name || '',
                pd: false, tp: false, rr: false,
                night: false, shift: '',
                hours: {}, total: 0,
                // The billable estimate on the ticket PDF prices OT hours at
                // the sheet's OT rate and passes per diem through at HEET's
                // paid amount — both are estimates, since payroll recomputes
                // them weekly.
                ot: 0, pdAmt: 0,
            };
        }
        const p = labor[id];
        if (!p.payClass && r.Pay_Class_Description) p.payClass = r.Pay_Class_Description;
        if (!p.crewLead && r.Foreman_Name) p.crewLead = r.Foreman_Name;
        // Shift comes from the HeavyJob timecard the hours were entered on
        // (1 = day, 2 = night), joined in by readHEETLabor.sql. NULL — an
        // equipment-only card or a pre-feed day — leaves the person dayside
        // with a blank Shift cell rather than claiming a shift nobody entered.
        if (Number(r.Shift) === 2) { p.night = true; p.shift = 'Nights'; }
        else if (Number(r.Shift) === 1 && !p.night) p.shift = 'Days';
        // The ticket's flag columns are the adder pay types, not hours.
        if (r.Record_Type === 'Per Diem') { p.pd = true; p.pdAmt += num(r.Amount); }
        else if (r.Record_Type === 'Truck Pay') p.tp = true;
        else if (r.Record_Type === 'Rig Pay') p.rr = true;
        // Only ST and OT carry hours; burden and adders repeat the shift
        // length in other columns and must not land on the ticket.
        if (r.Record_Type !== 'Regular' && r.Record_Type !== 'Overtime') return;
        const h = num(r.Hours);
        if (!h) return;
        if (r.Record_Type === 'Overtime') p.ot += h;
        p.hours[r.Cost_Code] = (p.hours[r.Cost_Code] || 0) + h;
        p.total += h;
        if (!codes[r.Cost_Code]) codes[r.Cost_Code] = r.Cost_Code_Description || '';
    });
    const overrides = opts.overrides || {};
    // Someone with adders and no hours (a per diem line with the hours cut
    // by the reviewed card, a standing adder on a 0-hour card line) is not
    // on the client's ticket; they are named in `dropped` for the checks.
    const worked = Object.values(labor).filter((p) => p.total > 0.005);
    const dropped = Object.values(labor).filter((p) => p.total <= 0.005)
        .map((p) => ({ id: p.id, name: p.name, adders: ['pd', 'tp', 'rr'].filter((k) => p[k]) }));
    return {
        codes: Object.keys(codes).sort().map((c) => ({ code: c, desc: codes[c] })),
        dropped,
        labor: worked
            .map((p) => {
                const m = resolveTier3(p.payClass, ratemap);
                // The field's ticket workbook (Staff List Tier 4) names each
                // person's billed classification directly — when it covers
                // the person it wins over the pay-class map resolution.
                const fieldCls = opts.staffCls ? opts.staffCls[String(p.id)] : null;
                const out = { ...p, cls: fieldCls || m.cls || p.payClass };
                // A '~'-prefixed Staff_ID is a synthetic key for someone with
                // no employee number (the Custom Ticket view's hand-entered
                // rows) — group by it, but never print it on the ticket.
                if (String(p.id).startsWith('~')) out.id = '';
                // A stored override beats the feed either way — including
                // pinning someone to Days whose card said night.
                const ov = overrides[String(p.id)];
                if (ov === 'Night') { out.night = true; out.shift = 'Nights'; }
                else if (ov === 'Day') { out.night = false; out.shift = 'Days'; }
                return out;
            })
            // Day crew first, night crew after — the ticket prints them as
            // two sections, day shift on top. Within a crew, most hours
            // first, then by name: the packet scripts' order, which puts the
            // full-shift crew together and short days (orientation, a
            // half-day office visit) at the bottom where they get read.
            .sort((a, b) => (a.night - b.night) || (b.total - a.total) || String(a.name).localeCompare(String(b.name))),
        equipment: Object.values(equipment)
            .sort((a, b) => String(a.unit || a.desc).localeCompare(String(b.unit || b.desc))),
    };
};

/**
 * The ticket's labor grid as a row plan: when a night crew exists the list
 * becomes two sections — a DAY SHIFT band, the day people, a blank spacer,
 * a NIGHT SHIFT band, the night people. Entries are a person, { label } for
 * a section band, or null for the spacer row. A day with no night cards (or
 * no shift data at all) stays the flat list it always was.
 */
/**
 * The ticket, cut down to one shift's deliverable: that crew's rows, only
 * the phase codes they touched, and the equipment placement rule the PDF
 * export has always used — equipment hours carry no shift in HEET, so
 * equipment rides the day sheet and the night sheet is labor only, except
 * on a night-only day, where it rides the only sheet there is. No shift
 * asked for returns the whole day untouched.
 */
export const ticketForShift = (t, shift) => {
    if (!shift) return t;
    const night = shift === 'Night';
    const labor = t.labor.filter((p) => Boolean(p.night) === night);
    const hasDay = t.labor.some((p) => !p.night);
    const hasNight = t.labor.some((p) => p.night);
    const nightOnly = hasNight && !hasDay;
    const equipment = (night ? nightOnly : !nightOnly) ? t.equipment : [];
    const used = new Set();
    labor.forEach((p) => Object.keys(p.hours).forEach((c) => { if (p.hours[c]) used.add(c); }));
    equipment.forEach((e) => Object.keys(e.hours).forEach((c) => { if (e.hours[c]) used.add(c); }));
    return { codes: t.codes.filter((c) => used.has(c.code)), labor, equipment };
};

export const laborSections = (labor) => {
    const night = (labor || []).filter((p) => p.night);
    if (!night.length) return [...(labor || [])];
    const day = labor.filter((p) => !p.night);
    return day.length
        ? [{ label: 'DAY SHIFT' }, ...day, null, { label: 'NIGHT SHIFT' }, ...night]
        : [{ label: 'NIGHT SHIFT' }, ...night];
};


// ── Field Ticket file ──
// The export fills the client's own workbook rather than rebuilding it: a
// Python script (pythonfunctions/make_field_ticket_template.py) trims their
// "Multi Phase Field Ticket - VoltaGrid.xlsx" down to its two ticket sheets
// ("Printable Time Ticket" up front, "Base Time Ticket" behind it) with the
// fill-in regions cleared; the trimmed file ships with the app at
// public/templates/field-ticket-template.xlsx; and this function patches
// values straight into that file's sheet XML at the zip level. The output
// is byte-identical to the client's template - logos, 686 merges, borders,
// grey tints, hidden SP column, print setup - except for the cells filled.
// (No spreadsheet library re-serializes it: exceljs chokes on the
// template's drawing parts and would degrade them anyway.) Rerun the
// Python script and commit the result whenever the client revises their
// workbook.
//
// Module-level (and exported) so the ticket can be built and checked
// outside the component; tests pass the template bytes via templateData
// because jsdom has no fetch. Returns { buffer, overflow } or null when
// the day has nothing to ticket.
export const ticketOverflowWarning = (o) => (
    o && (o.labor || o.equipment || o.codes)
        ? `Day exceeds the client's ticket grid - clamped (${o.labor} people, ${o.equipment} equipment, ${o.codes} phase codes over).`
        : null
);

// Shared template plumbing for the two client-workbook exports (field ticket
// and force report). Every cell patched exists in the template XML (they all
// carry styles), so a patch is an in-place swap of one <c> element: numbers
// get a <v>, strings become inline strings. Nothing else in the file changes
// - which is the whole point.
const xmlEsc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const patchSheetXml = (xml, cells) => {
    let out = xml;
    Object.entries(cells).forEach(([ref, v]) => {
        if (v === null || v === undefined || v === '') return;
        const re = new RegExp(`<c r="${ref}"([^>]*?)(?:/>|>[\\s\\S]*?</c>)`);
        const m = re.exec(out);
        if (!m) return;
        const attrs = m[1].replace(/ t="[^"]*"/, '');
        const rep = typeof v === 'number'
            ? `<c r="${ref}"${attrs}><v>${v}</v></c>`
            : `<c r="${ref}"${attrs} t="inlineStr"><is><t xml:space="preserve">${xmlEsc(v)}</t></is></c>`;
        out = out.slice(0, m.index) + rep + out.slice(m.index + m[0].length);
    });
    return out;
};
// Excel day serial; the template cell's own date format renders it.
const excelSerial = (date) => (date ? moment(date, 'YYYY-MM-DD').diff(moment('1899-12-30', 'YYYY-MM-DD'), 'days') : null);
const loadTemplateZip = async (name, templateData) => {
    const JSZip = (await import('jszip')).default;
    const bytes = templateData || await (async () => {
        const res = await fetch(`${process.env.PUBLIC_URL || ''}/templates/${name}`);
        if (!res.ok) throw new Error(`${name} missing from public/templates`);
        return res.arrayBuffer();
    })();
    return JSZip.loadAsync(bytes);
};

export const buildFieldTicketFile = async ({
    rows, ratemap, date, jobKey, jobName, client, templateData, shift, overrides, staffCls, equipName,
}) => {
    const t = ticketForShift(ticketFrom(rows, ratemap, { overrides, staffCls, equipName }), shift);
    if (!t.labor.length && !t.equipment.length) return null;

    const zip = await loadTemplateZip('field-ticket-template.xlsx', templateData);

    const CL = (n) => { let s = ''; let x = n; while (x > 0) { s = String.fromCharCode(65 + ((x - 1) % 26)) + s; x = Math.floor((x - 1) / 26); } return s; };
    const asNum = (v) => (v !== '' && v !== null && Number.isFinite(Number(v)) ? Number(v) : v);
    const h2 = (v) => (v ? Number(v.toFixed(2)) : null);
    const serial = excelSerial(date);
    const desc = t.codes.map((c) => `${c.code}: ${c.desc}`).join('\n');

    // The template's fixed grids. Anything past them is clamped rather than
    // risking a row insert through 686 merged ranges - callers surface
    // ticketOverflowWarning(result.overflow).
    const CAP = { baseLabor: 102, baseEquip: 74, prLabor: 80, prEquip: 81, codes: 14 };
    // The shift bands and their spacer occupy grid rows too, so the plan
    // length (not the headcount) is what gets clamped against the template.
    const laborPlan = laborSections(t.labor);
    const overflow = {
        labor: Math.max(0, laborPlan.length - CAP.prLabor),
        equipment: Math.max(0, t.equipment.length - CAP.baseEquip),
        codes: Math.max(0, t.codes.length - CAP.codes),
    };
    const codes = t.codes.slice(0, CAP.codes);
    const patchSheet = patchSheetXml;

    // ── Printable Time Ticket (sheet1) ──
    const pr = {
        H3: serial, H4: asNum(jobKey), L4: jobName || null, H5: client || null, B12: desc,
    };
    laborPlan.slice(0, CAP.prLabor).forEach((p, i) => {
        const r = 15 + i;
        if (!p) return;                       // spacer row between shifts
        if (p.label) { pr[`A${r}`] = p.label; return; }
        pr[`A${r}`] = p.name;                 // A:F
        pr[`G${r}`] = asNum(p.id);            // G:I
        pr[`J${r}`] = p.cls;                  // J:U
        if (p.pd) pr[`W${r}`] = 'X';          // W - the P column
        if (p.tp) pr[`X${r}`] = 'X';
        if (p.rr) pr[`Y${r}`] = 'X';
        pr[`Z${r}`] = h2(p.total);            // Z:AB
    });
    pr.Z96 = h2(t.labor.reduce((a, p) => a + p.total, 0));
    t.equipment.slice(0, CAP.prEquip).forEach((e, i) => {
        const r = 99 + i;
        pr[`A${r}`] = e.desc;                 // A:H
        pr[`I${r}`] = e.unit;                 // I:N
        pr[`Z${r}`] = h2(e.total);            // Z:AB
        if (e.rental) pr[`AC${r}`] = 'Rental';
    });
    pr.Z180 = h2(t.equipment.reduce((a, e) => a + e.total, 0));

    // ── Base Time Ticket (sheet2): the phase-hours matrix ──
    const bs = { B3: serial, B4: asNum(jobKey), B5: jobName || null, B6: client || null, A9: desc };
    codes.forEach((c, i) => {
        bs[`${CL(11 + i)}9`] = c.desc;
        bs[`${CL(11 + i)}10`] = asNum(c.code);
    });
    const colTot = {};
    laborPlan.slice(0, CAP.baseLabor).forEach((p, i) => {
        const r = 11 + i;
        if (!p) return;                       // spacer row between shifts
        if (p.label) { bs[`A${r}`] = p.label; return; }
        bs[`A${r}`] = p.name;
        bs[`B${r}`] = asNum(p.id);
        bs[`C${r}`] = p.cls;
        bs[`D${r}`] = p.shift || null;        // the template's Shift column
        bs[`E${r}`] = p.crewLead;
        if (p.pd) bs[`G${r}`] = 'X';
        if (p.tp) bs[`H${r}`] = 'X';
        if (p.rr) bs[`I${r}`] = 'X';
        // The J "Hours" column has to carry the row's hours too: the client's
        // sheet flags every row red where (J + Total)/2 differs from the sum
        // of its phase cells, so leaving J blank turns the whole matrix red.
        bs[`J${r}`] = h2(p.total);
        codes.forEach((c, k) => {
            const v = h2(p.hours[c.code]);
            if (v !== null) { bs[`${CL(11 + k)}${r}`] = v; colTot[c.code] = (colTot[c.code] || 0) + v; }
        });
        bs[`Y${r}`] = h2(p.total);
    });
    codes.forEach((c, k) => { bs[`${CL(11 + k)}113`] = h2(colTot[c.code]); });
    bs.Y113 = h2(t.labor.reduce((a, p) => a + p.total, 0));
    const eqTot = {};
    t.equipment.slice(0, CAP.baseEquip).forEach((e, i) => {
        const r = 116 + i;
        bs[`A${r}`] = e.desc;
        bs[`B${r}`] = e.unit;
        bs[`J${r}`] = h2(e.total);
        codes.forEach((c, k) => {
            const v = h2(e.hours[c.code]);
            if (v !== null) { bs[`${CL(11 + k)}${r}`] = v; eqTot[c.code] = (eqTot[c.code] || 0) + v; }
        });
        bs[`Y${r}`] = h2(e.total);
        if (e.rental) bs[`Z${r}`] = 'Rental';
    });
    codes.forEach((c, k) => { bs[`${CL(11 + k)}190`] = h2(eqTot[c.code]); });
    bs.Y190 = h2(t.equipment.reduce((a, e) => a + e.total, 0));

    // sheet1 = Printable Time Ticket, sheet2 = Base Time Ticket (the
    // template script saves them in that order).
    // createFolders:false — otherwise JSZip adds xl/ and xl/worksheets/
    // directory entries the client's file never had.
    const opts = { createFolders: false };
    zip.file('xl/worksheets/sheet1.xml', patchSheet(await zip.file('xl/worksheets/sheet1.xml').async('string'), pr), opts);
    zip.file('xl/worksheets/sheet2.xml', patchSheet(await zip.file('xl/worksheets/sheet2.xml').async('string'), bs), opts);
    const buffer = await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
    return { buffer, overflow };
};

// ── Daily Force Report file ──
// Same template-patch approach as the field ticket: the client's own
// "Attendance & Force Report - Master" workbook, trimmed to its
// "VoltaGrid MP Report" sheet by pythonfunctions/make_force_report_template.py
// (which also adds the DocuSign signature block) and shipped at
// public/templates/force-report-template.xlsx. The client's sheet computes
// its counts with SUMPRODUCTs over an attendance grid this file no longer
// carries, so every cell that showed a number gets a literal value — heads
// under Job Site, zeros elsewhere (their number format renders 0 as "-",
// exactly like the master).
//
// Craft rows are fixed: TIER3's nine Direct crafts land on rows 12-20 of the
// left band, its ten Indirect crafts on rows 12-21 of the right, in TIER3
// order — the client's form and the tier list are transcriptions of each
// other. Unclassified people have no row on the client's form; they are
// EXCLUDED from the file and reported back in the result so the caller can
// warn instead of quietly under-counting.
export const forceUnclassifiedWarning = (n) => (
    n ? `${n} ${n === 1 ? 'person is' : 'people are'} unclassified and NOT on the client's Force Report — fix the classification mapping first.` : null
);

// ── Equipment sheet ──
// The force report's second sheet: equipment on site that day, one row per
// rate sheet item with its count (billingEquipLog equipLogRollup), and
// nothing else. The client's template has no such sheet, so the part is
// written here — the sheet XML, its workbook entry, rel and content type,
// and six cell styles appended to the template's styles.xml.
const EQUIP_SHEET = 'Equipment';
const appendStyles = (xml) => {
    let out = xml;
    const add = (tag, items) => {
        const m = new RegExp(`<${tag} count="(\\d+)"`).exec(out);
        const base = Number(m[1]);
        out = out.replace(m[0], `<${tag} count="${base + items.length}"`)
            .replace(`</${tag}>`, `${items.join('')}</${tag}>`);
        return base;
    };
    const font = (b, sz) => `<font>${b ? '<b/>' : ''}<sz val="${sz}"/><color rgb="FF000000"/><name val="Arial"/><family val="2"/></font>`;
    const f = add('fonts', [font(true, 12), font(false, 10), font(true, 10)]);
    const fill = add('fills', ['<fill><patternFill patternType="solid"><fgColor rgb="FFD9D9D9"/><bgColor indexed="64"/></patternFill></fill>']);
    const bd = add('borders', ['<border><left/><right/><top/><bottom style="thin"><color auto="1"/></bottom><diagonal/></border>']);
    const xf = (fontId, { fillId = 0, borderId = 0, h } = {}) => `<xf numFmtId="0" fontId="${fontId}" fillId="${fillId}" borderId="${borderId}" xfId="0" applyFont="1"`
        + `${fillId ? ' applyFill="1"' : ''}${borderId ? ' applyBorder="1"' : ''} applyAlignment="1"><alignment vertical="center"${h ? ` horizontal="${h}"` : ''}/></xf>`;
    const s = add('cellXfs', [
        xf(f),                                           // title
        xf(f + 1),                                       // job / date line
        xf(f + 2, { fillId: fill, borderId: bd }),       // header
        xf(f + 2, { fillId: fill, borderId: bd, h: 'center' }),
        xf(f + 1, { borderId: bd }),                     // item
        xf(f + 1, { borderId: bd, h: 'center' }),        // count
    ]);
    return { xml: out, s: { title: s, sub: s + 1, head: s + 2, headC: s + 3, item: s + 4, count: s + 5 } };
};

const addEquipmentSheet = async (zip, { items, date, jobKey, jobName }) => {
    const opts = { createFolders: false };
    const styled = appendStyles(await zip.file('xl/styles.xml').async('string'));
    zip.file('xl/styles.xml', styled.xml, opts);
    const { s } = styled;

    const str = (ref, v, st) => `<c r="${ref}" s="${st}" t="inlineStr"><is><t xml:space="preserve">${xmlEsc(v)}</t></is></c>`;
    const num = (ref, v, st) => `<c r="${ref}" s="${st}"><v>${v}</v></c>`;
    const row = (r, cells, ht) => `<row r="${r}"${ht ? ` ht="${ht}" customHeight="1"` : ''}>${cells.join('')}</row>`;
    const day = date ? moment(date, 'YYYY-MM-DD').format('M/D/YY') : '';
    const rowsXml = [
        row(1, [str('A1', 'Equipment on Site', s.title)], 18),
        row(2, [str('A2', [jobKey, jobName].filter(Boolean).join(' - '), s.sub), str('B2', day, s.sub)]),
        row(3, [str('A3', 'Rate Sheet Item', s.head), str('B3', 'Count', s.headC)]),
        ...items.map((it, i) => row(4 + i, [str(`A${4 + i}`, it.name, s.item), num(`B${4 + i}`, it.count, s.count)])),
    ];
    const last = 3 + items.length;
    zip.file('xl/worksheets/sheet2.xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
        + '<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>'
        + `<dimension ref="A1:B${last}"/>`
        + '<sheetViews><sheetView showGridLines="0" workbookViewId="0"/></sheetViews>'
        + '<sheetFormatPr defaultRowHeight="15"/>'
        + '<cols><col min="1" max="1" width="60" customWidth="1"/><col min="2" max="2" width="10" customWidth="1"/></cols>'
        + `<sheetData>${rowsXml.join('')}</sheetData>`
        + '<pageMargins left="0.5" right="0.5" top="0.5" bottom="0.5" header="0.3" footer="0.3"/>'
        + '<pageSetup orientation="portrait" fitToWidth="1" fitToHeight="0"/>'
        + '</worksheet>', opts);

    const wb = await zip.file('xl/workbook.xml').async('string');
    zip.file('xl/workbook.xml', wb.replace('</sheets>',
        `<sheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" name="${EQUIP_SHEET}" sheetId="2" state="visible" r:id="rIdEquip"/></sheets>`), opts);
    const rels = await zip.file('xl/_rels/workbook.xml.rels').async('string');
    zip.file('xl/_rels/workbook.xml.rels', rels.replace('</Relationships>',
        '<Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="/xl/worksheets/sheet2.xml" Id="rIdEquip"/></Relationships>'), opts);
    const types = await zip.file('[Content_Types].xml').async('string');
    zip.file('[Content_Types].xml', types.replace('</Types>',
        '<Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>'), opts);
};

// equipment: billingEquipLog equipLogRollup(...).items — [{ name, count }].
// Empty or missing leaves the workbook as the client's single sheet.
export const buildForceReportFile = async ({ rows, ratemap, date, jobKey, jobName, client, shift = 'Day', templateData, equipment }) => {
    const groups = rollUp(peopleFrom(rows, ratemap));
    if (!groups.length) return null;

    const zip = await loadTemplateZip('force-report-template.xlsx', templateData);

    const heads = {};
    let unclassified = 0;
    groups.forEach((g) => {
        if (g.key === 'unclassified') unclassified += g.head;
        else heads[g.key] = g.head;
    });
    const direct = TIER3.filter((t) => t.dir === 'Direct');
    const indirect = TIER3.filter((t) => t.dir === 'Indirect');
    const sumOf = (list) => list.reduce((a, t) => a + (heads[String(t.code)] || 0), 0);
    const directTotal = sumOf(direct);
    const indirectTotal = sumOf(indirect);

    const serial = excelSerial(date);
    // Run stamp, with the time-of-day fraction (M1 reads "Run time===>").
    const now = moment();
    const runSerial = now.clone().startOf('day').diff(moment('1899-12-30', 'YYYY-MM-DD'), 'days')
        + (now.diff(now.clone().startOf('day'), 'seconds') / 86400);

    const cells = {
        N1: Number(runSerial.toFixed(6)),
        D7: 'Strike', L7: jobName || null,
        I9: shift, M9: serial,
        // INPUTS panel (page 2 of the print layout) — same values, so the
        // sheet still reads coherently if the client prints both pages.
        T12: serial, T13: jobName || null, T14: 'Strike',
        T15: Number.isFinite(Number(jobKey)) ? Number(jobKey) : String(jobKey),
        T16: shift, T17: client || null,
    };
    // Direct band rows 12-20 (row 21 is Sub-Contractor), Indirect rows 12-21.
    // Job Site carries the heads; Fab Shop / Other / Absent / Hired /
    // Terminated have no source in HEET and print the form's "-" for 0.
    direct.forEach((t, i) => {
        const r = 12 + i;
        const n = heads[String(t.code)] || 0;
        cells[`B${r}`] = n; cells[`C${r}`] = 0; cells[`D${r}`] = 0; cells[`E${r}`] = n;
        cells[`F${r}`] = 0; cells[`G${r}`] = 0; cells[`H${r}`] = 0;
    });
    ['B21', 'C21', 'D21', 'E21', 'F21', 'G21', 'H21'].forEach((ref) => { cells[ref] = 0; });
    indirect.forEach((t, i) => {
        const r = 12 + i;
        const n = heads[String(t.code)] || 0;
        cells[`J${r}`] = n; cells[`K${r}`] = 0; cells[`L${r}`] = 0; cells[`M${r}`] = n;
        cells[`N${r}`] = 0; cells[`O${r}`] = 0; cells[`P${r}`] = 0;
    });
    cells.B22 = directTotal; cells.C22 = 0; cells.D22 = 0; cells.E22 = directTotal;
    cells.F22 = 0; cells.G22 = 0; cells.H22 = 0;
    cells.J22 = indirectTotal; cells.K22 = 0; cells.L22 = 0; cells.M22 = indirectTotal;
    cells.N22 = 0; cells.O22 = 0; cells.P22 = 0;
    cells.M25 = directTotal; cells.M26 = indirectTotal;
    cells.M27 = 0; cells.M28 = 0; cells.M29 = 0;
    cells.M30 = directTotal + indirectTotal;

    const opts = { createFolders: false };
    zip.file('xl/worksheets/sheet1.xml', patchSheetXml(await zip.file('xl/worksheets/sheet1.xml').async('string'), cells), opts);
    if ((equipment || []).length) await addEquipmentSheet(zip, { items: equipment, date, jobKey, jobName });
    const buffer = await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
    return { buffer, unclassified };
};

